// dashboard/dashboard.service.ts
import { api } from './api';
import type { ApiResponse } from '../utils/types';
import { toast } from 'sonner';

// API endpoints
const API_ENDPOINTS = {
  overview: '/dashboard/overview',
  stats: '/dashboard/stats',
  activities: '/dashboard/activities',
  deadlines: '/dashboard/deadlines',
};

// Type guard for error with response
function isErrorWithResponse(error: unknown): error is { response?: { status?: number; data?: { message?: string } } } {
  return typeof error === 'object' && error !== null && 'response' in error;
}

// Dashboard Service
export class DashboardService {
  // Get dashboard overview data
  async getOverview(): Promise<DashboardOverview> {
    try {
      const response = await api.get<ApiResponse<DashboardOverview>>(API_ENDPOINTS.overview);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch dashboard overview');
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch dashboard overview:', error);
      const message = isErrorWithResponse(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to fetch dashboard overview. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  // Get dashboard statistics
  async getStats(): Promise<DashboardStats> {
    try {
      const response = await api.get<ApiResponse<DashboardStats>>(API_ENDPOINTS.stats);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch dashboard stats');
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      const message = isErrorWithResponse(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to fetch dashboard stats. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  // Get recent activities
  async getActivities(limit?: number): Promise<ActivityItem[]> {
    try {
      const params = limit ? `?limit=${limit}` : '';
      const response = await api.get<ApiResponse<ActivityItem[]>>(`${API_ENDPOINTS.activities}${params}`);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch activities');
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch activities:', error);
      const message = isErrorWithResponse(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to fetch activities. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  // Get upcoming deadlines
  async getDeadlines(limit?: number): Promise<DeadlineItem[]> {
    try {
      const params = limit ? `?limit=${limit}` : '';
      const response = await api.get<ApiResponse<DeadlineItem[]>>(`${API_ENDPOINTS.deadlines}${params}`);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch deadlines');
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch deadlines:', error);
      const message = isErrorWithResponse(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to fetch deadlines. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }
}

// Types
export interface ProjectAnalyticsItem {
  day: string;
  height: string;
  active: boolean;
  value: number;
}

export interface TeamMemberItem {
  id: string;
  name: string;
  avatar?: string;
  role: string;
  status: 'Online' | 'Offline' | 'Away';
  currentTask?: string;
}

export interface ProjectCardItem {
  id: string;
  title: string;
  dueDate: string;
  progress: number;
}

export interface DashboardOverview {
  stats: DashboardStats;
  activities: ActivityItem[];
  deadlines: DeadlineItem[];
  projectAnalytics: ProjectAnalyticsItem[];
  teamMembers: TeamMemberItem[];
  featuredProject?: ProjectCardItem;
}

export interface DashboardStats {
  totalPipelineValue: number;
  completedRevenue: number;
  pendingRevenue: number;
  activePartnerships: number;
  pipelineValueChange?: number;
  completedRevenueChange?: number;
  pendingRevenueChange?: number;
  activePartnershipsChange?: number;
}

export interface ActivityItem {
  id: string;
  type: 'partnership' | 'content' | 'analytics' | 'project' | 'task' | 'other';
  title: string;
  description: string;
  time: string;
  createdAt: string;
  metadata?: Record<string, any>;
}

export interface DeadlineItem {
  id: string;
  title: string;
  date: string;
  dueDate: string;
  priority: 'high' | 'medium' | 'low';
  type: 'delivery' | 'contract' | 'report' | 'task' | 'other';
  projectId?: string;
  partnershipId?: string;
}

export const dashboardService = new DashboardService();

